import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const AttendanceSummary = ({ records }) => {
  // Calculate real statistics from attendance records
  const statistics = useMemo(() => {
    if (!records || records?.length === 0) {
      return {
        totalPresent: 0,
        totalAbsent: 0,
        totalHalfDay: 0,
        lateArrivals: 0,
        earlyDepartures: 0,
        onLeave: 0
      };
    }

    // CRITICAL DEBUGGING: Count status distribution
    const statusDistribution = {};
    records?.forEach(record => {
      const status = record?.attendanceStatus || record?.attendance_status || 'unknown';
      statusDistribution[status] = (statusDistribution?.[status] || 0) + 1;
    });
    
    console.log('📊 ATTENDANCE SUMMARY - Status Distribution:', statusDistribution);
    console.log('🎯 Half day count from raw check:', 
      records?.filter(r => 
        r?.attendanceStatus === 'halfday' || r?.attendance_status === 'halfday' ||
        r?.attendanceStatus === 'half_day' || r?.attendance_status === 'half_day' ||
        r?.attendanceStatus === 'halfDay' || r?.attendance_status === 'halfDay'
      )?.length
    );

    const stats = {
      totalPresent: 0,
      totalAbsent: 0,
      totalHalfDay: 0,
      lateArrivals: 0,
      earlyDepartures: 0,
      onLeave: 0
    };

    records?.forEach(record => {
      const status = record?.attendanceStatus || record?.attendance_status;
      
      switch (status) {
        case 'present':
          stats.totalPresent++;
          break;
        case 'absent':
          stats.totalAbsent++;
          break;
        case 'halfday': case 'half_day': case 'halfDay':
          stats.totalHalfDay++;
          console.log('✅ Counted half day record in summary:', record);
          break;
        case 'late':
          stats.lateArrivals++;
          break;
        case 'early_departure':
          stats.earlyDepartures++;
          break;
        case 'on_leave': case'onLeave':
          stats.onLeave++;
          break;
        default:
          console.warn(`⚠️ Unknown status in summary: "${status}"`);
          break;
      }
    });

    console.log('✅ Final attendance statistics:', stats);
    return stats;
  }, [records]);

  const summaryCards = [
    {
      title: 'Total Present',
      value: statistics?.totalPresent || 0,
      icon: 'Users',
      color: 'success',
      description: 'Employees marked present'
    },
    {
      title: 'Total Absent',
      value: statistics?.totalAbsent || 0,
      icon: 'UserX',
      color: 'error',
      description: 'Employees marked absent'
    },
    {
      title: 'Half Day',
      value: statistics?.totalHalfDay || 0,
      icon: 'Clock',
      color: 'warning',
      description: 'Employees on half day'
    },
    {
      title: 'Late Arrivals',
      value: statistics?.lateArrivals || 0,
      icon: 'Clock',
      color: 'warning',
      description: 'Late check-ins'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      success: 'bg-success/10 text-success',
      warning: 'bg-warning/10 text-warning',
      error: 'bg-error/10 text-error',
      primary: 'bg-primary/10 text-primary'
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {summaryCards?.map((card, index) => (
          <div key={index} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">{card?.title}</p>
                <p className="text-2xl font-bold text-foreground mt-2">{card?.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{card?.description}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(card?.color)}`}>
                <Icon name={card?.icon} size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Records</p>
              <p className="text-lg font-semibold text-foreground">{records?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="LogOut" size={20} color="var(--color-warning)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Early Departures</p>
              <p className="text-lg font-semibold text-foreground">{statistics?.earlyDepartures || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Briefcase" size={20} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">On Leave</p>
              <p className="text-lg font-semibold text-foreground">{statistics?.onLeave || 0}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceSummary;